/* eslint-disable camelcase */
import Qty from 'js-quantities';
import * as R from 'ramda';

import {
  roundValue,
} from './common';

/*
 * Example
 * 22
 * 22.2
 * 22-2.2
 * 22.22-22.2
 * */
export const numberMask = (rawValue = '') => {
  const split = rawValue.split('');
  const dashSplit = rawValue.split('-');
  const digitRegExp = /\d/;
  const mask = split.reduce((acc, symbol) => {
    let item = null;
    const dashIndex = acc.lastIndexOf('-');
    const pointIndex = acc.lastIndexOf('.');
    switch (symbol) {
      case '-':
        if (dashIndex === -1) {
          item = '-';
        }
        break;
      case '.':
        if (dashSplit.length === 1 && pointIndex === -1) {
          item = '.';
        } else if (pointIndex === -1 || pointIndex < dashIndex) {
          item = '.';
        }
        break;
      default:
        if (digitRegExp.test(symbol)) {
          item = digitRegExp;
        }
    }

    return item ? [...acc, item] : acc;
  }, []);

  return mask;
};

export const imperialToMetricUnits = {
  in: 'cm',
  ft: 'm',
  mi: 'km',
  lbs: 'kg',
};

export const metricToImperialUnits = {
  ...R.invertObj(imperialToMetricUnits),
  mm: 'ft', // TODO
};

export const getConvertedUnitName = (name: string, unitSystem: string) => {
  const units = unitSystem === 'imperial'
    ? metricToImperialUnits
    : imperialToMetricUnits;

  return units[name] || name;
};

export const getPropertyValue = (property: any, unit: string) => {
  if (!property || !property.value) {
    return '';
  }

  if (unit === 's' || unit === 'mmss') {
    return property.value;
  }

  let { value, value2 } = property;

  const saveUnit = property.exercise_property
    ? property.exercise_property.personal_property?.save_unit?.short_name
    : property.personal_property?.save_unit?.short_name;

  if (unit && unit !== saveUnit) {
    const convert = Qty.swiftConverter(saveUnit, unit);
    value = roundValue(convert(value), 1);
    value2 = value2 ? ` - ${roundValue(convert(value2), 1)}` : '';
  } else {
    value = roundValue(value, 1);
    value2 = value2 ? ` - ${roundValue(value2, 1)}` : '';
  }

  return `${value}${value2}`;
};

export const convertPropertyRawValue = (
  rawValue: null | string | number,
  unit: string,
  saveUnit: string,
) => {
  let value = rawValue;
  let value2 = null;

  if (typeof rawValue === 'string' && unit && unit !== 's' && unit !== 'mmss') {
    const ds: any[] = rawValue.split('-');
    if (saveUnit && unit !== saveUnit) {
      const convert = Qty.swiftConverter(unit, saveUnit);
      value = roundValue(convert(ds[0]), 5);
      value2 = ds[1] ? roundValue(convert(ds[1]), 5) : null;
    } else {
      value = roundValue(ds[0], 2);
      value2 = ds[1] ? roundValue(ds[1], 2) : null;
    }
  }

  return {
    value,
    value2,
  };
};

export const formatPropertyTime = (time: number) => {
  const mind = time % (60 * 60);
  const minutes = Math.floor(mind / 60);

  const secd = mind % 60;
  const seconds = Math.ceil(secd);

  const m = minutes > 0 ? `${minutes}mins` : '';
  const s = seconds > 0 ? `${seconds}s` : '';
  return `${m} ${s}`;
};

export const getUnitByUnitSystem = (property: any, unitSystem: string): any => {
  const unit = property.default_unit;
  const unitName = getConvertedUnitName(unit.short_name, unitSystem);

  if (unitName === unit.short_name) {
    return unit;
  }

  return property.property_units.find((u: any) => u.short_name === unitName) || unit;
};

export const getUnitsByUnitSystem = (propertyUnits: any[], unitSystem: string): any => {
  const units = unitSystem === 'imperial'
    ? imperialToMetricUnits
    : metricToImperialUnits;

  return propertyUnits.filter(u => units[u.short_name]);
};
